import { CancelTokenSource } from 'axios'
import { ObjectUtils } from '@/commons/utils/objects'
import { StringUtils } from '@/commons/utils/string'
import { Action, Module, VuexModule } from 'vuex-class-modules'
import { VuexRegisterGlobals } from '@/store/decorators/vuex.register.globals'

import {
    ApplicationDocumentEntity,
    ApplicationEntity,
    GeneralError
} from '@/store/entities'
import {
    Rr,
    RrPerson,
    Biometry,
    Application,
    ApplicationContact,
    ApplicationBiometry,
    ApplicationDocument,
    ApplicationApplicant
} from '@/commons/types'
import { applicantStore, userStore } from '@/store'
import {
    ApplicantApplierRelationType,
    ValidDocumentType
} from '@/commons/constants/application'
import { DocumentResponse } from '@/commons/types/document'
import { ApplicationReason, Country } from '@/commons/constants/classifier'

@VuexRegisterGlobals({
    actionParams: [{
        targetActionName: 'clearAll',
        contextActionName: 'clear'
    }]
})
@Module({ generateMutationSetters: true })
export default class Applicant extends VuexModule {
    rrInfo: Rr | null = null
    stateFee: number | null = null
    biometry: Biometry | null = null
    application: Application = new ApplicationEntity()
    requestCancelToken: CancelTokenSource | null = null
    fingerprintsCaptured: string | null | undefined = undefined
    isPhotoPreliminaryCheckRunning = false
    isApplicationProceedBlocked = false
    validDocuments: Array<DocumentResponse> | null = null
    readonly DOCUMENT_TYPES_TO_CHECK_FOR_MEDIATED_ISSUANCE: Array<string> = [
        ValidDocumentType.CITIZEN_PASSPORT.toString(),
        ValidDocumentType.ID_CARD.toString(),
        ValidDocumentType.ADDITIONAL_PASSPORT.toString(),
        ValidDocumentType.RESIDENCE_CARD.toString(),
        ValidDocumentType.ALIEN_PASSPORT.toString(),
        ValidDocumentType.SEAFARERS_DISCHARGE_BOOK.toString(),
        ValidDocumentType.CERTIFICATE_OF_SERVICE_ON_SHIPS.toString()
    ]

    get citizenPassport (): ApplicationDocument {
        if (!this.getApplication.citizenPassport) {
            this.getApplication.citizenPassport = new ApplicationDocumentEntity()
        }
        return this.getApplication.citizenPassport
    }

    get idCard (): ApplicationDocument {
        if (!this.getApplication.idCard) {
            this.getApplication.idCard = new ApplicationDocumentEntity()
        }
        return this.getApplication.idCard
    }

    get applicationApplicant (): ApplicationApplicant {
        return this.getApplication.applicant
    }

    get applicationContact (): ApplicationContact {
        return this.getApplication.contact
    }

    get applicationBiometry (): ApplicationBiometry {
        return this.getApplication.biometry
    }

    get getApplication (): Application {
        return this.application
    }

    get isValidApplicant (): boolean {
        return this.isValidRrInfo && this.isValidFingerprints && this.isValidApplication
    }

    get isValidApplication (): boolean {
        return !!(this.getApplication.uuid === null || this.getApplication.uuid)
    }

    get isValidFingerprints (): boolean {
        return !!(this.fingerprintsCaptured === null || this.fingerprintsCaptured)
    }

    get isValidRrInfo (): boolean {
        return !ObjectUtils.isEmpty(this.rrInfo)
    }

    get rrPersonBirthCountry (): string {
        return this.rrInfo?.personData.birthCountry || ''
    }

    get personCode (): string {
        if (!this.rrInfo?.personData?.personCode) {
            throw new GeneralError(['errors.system.rrInfo.invalid'])
        }
        return this.rrInfo.personData.personCode
    }

    get addressWithPostalCode (): string {
        let address = ''
        if (this.applicationContact.address.fullAddress != null) {
            if (this.applicationContact.address.country === Country.ESTONIA) {
                address = this.applicationContact.address.stateOrProvinceOrCounty
                    ? this.applicationContact.address.fullAddress
                    : StringUtils.composeAddress([
                        this.applicationContact.address.fullAddress,
                        this.applicationContact.address.postalCode
                    ])
            } else {
                address = this.applicationContact.address.fullAddress
            }
        }
        return address || ''
    }

    get father (): RrPerson | null {
        return this.rrInfo?.father ? this.rrInfo.father : null
    }

    get mother (): RrPerson | null {
        return this.rrInfo?.mother ? this.rrInfo.mother : null
    }

    get applicantAge (): number | null {
        return typeof this.rrInfo?.personData?.age === 'number' ? this.rrInfo?.personData?.age : null
    }

    get applicantFullName (): string {
        if (!this.rrInfo?.personData?.firstName || !this.rrInfo?.personData?.lastName) {
            throw new GeneralError(['errors.system.rrInfo.invalid'])
        }
        return this.rrInfo.personData.firstName + ' ' + this.rrInfo.personData.lastName
    }

    get applicantRelationWithApplier (): ApplicantApplierRelationType {
        let relationResult = ApplicantApplierRelationType.APPLICANT

        if (this.isApplierGuardian()) {
            relationResult = ApplicantApplierRelationType.WARD
        } else if (this.isApplierParent()) {
            relationResult = ApplicantApplierRelationType.CHILD
        }
        return relationResult
    }

    validDocumentsForRetrieval (validDocuments: Array<DocumentResponse>): Array<DocumentResponse> {
        const idCardReason = applicantStore.getApplication.idCard?.reason
        const passportReason = applicantStore.getApplication.citizenPassport?.reason

        if (validDocuments === null || validDocuments.length === 0) {
            return []
        }
        validDocuments = validDocuments.filter(validDocument => this.isDocumentTypeForMediatedIssuance(validDocument.documentType))
        if (idCardReason !== null && idCardReason === ApplicationReason.LOST_STOLEN_DESTROYED) {
            validDocuments = this.removeDocumentFromDocumentsForRetrieval(validDocuments, ValidDocumentType.ID_CARD)
        }
        if (passportReason !== null && passportReason === ApplicationReason.LOST_STOLEN_DESTROYED) {
            validDocuments = this.removeDocumentFromDocumentsForRetrieval(validDocuments, ValidDocumentType.CITIZEN_PASSPORT)
        }
        return validDocuments
    }

    removeDocumentFromDocumentsForRetrieval (validDocumentsForRetrieval: Array<DocumentResponse>, documentType: ValidDocumentType): Array<DocumentResponse> {
        return validDocumentsForRetrieval.filter(validDocument => validDocument.documentType !== documentType.toString())
    }

    isDocumentTypeForMediatedIssuance (documentType: string): boolean {
        return this.DOCUMENT_TYPES_TO_CHECK_FOR_MEDIATED_ISSUANCE.includes(documentType)
    }

    isApplierParent (): boolean {
        const userPersonCode = userStore.personCode
        const fatherPersonCode = this.father?.personCode
        const motherPersonCode = this.mother?.personCode
        return userPersonCode === fatherPersonCode || userPersonCode === motherPersonCode
    }

    isApplierGuardian (): boolean {
        const applicantGuardians = applicantStore.rrInfo?.guardians
        if (Array.isArray(applicantGuardians) && applicantGuardians.length > 0) {
            const userPersonCode = userStore.personCode
            for (const guardian of applicantGuardians) {
                if (guardian.personCode === userPersonCode) {
                    return true
                }
            }
        }
        return false
    }

    @Action
    clear (): void {
        this.rrInfo = null
        this.biometry = null
        this.stateFee = null
        this.requestCancelToken = null
        this.fingerprintsCaptured = undefined
        this.application = new ApplicationEntity()
        this.isPhotoPreliminaryCheckRunning = false
    }
}
